<template>
  <div>
    <vue-title title="Parental Consent Required - MyWhoosh"></vue-title>

    <section class="login-wrap">
      <div class="container">
        <div class="row">
          <section class="col-md-12 col-md-offset-2">
            <div class="login-box">
              <!-- Step 1: Ask for Parent Email -->
              <div v-if="!emailSent" class="parental-consent-form">
                <h3>Ask a Parent or Guardian for Help</h3>

                <div class="alert alert-warning">
                  <strong
                    ><i class="fa fa-exclamation-triangle"></i>
                    Important:</strong
                  >
                  You need to be above 18 years to create your own account. But
                  don't worry! you can still play with your parent's consent.
                </div>

                <div class="row">
                  <div class="col-md-12">
                    <p class="lead">
                      To continue, please enter your parent's email address
                    </p>

                    <form @submit.prevent="submitParentalConsent" class="mt-4">
                      <div class="form-group">
                        <label for="parentEmail" class="control-label"
                          >Parent's email address</label
                        >
                        <input
                          type="email"
                          class="form-control"
                          id="parentEmail"
                          v-model="parentEmail"
                          placeholder="parent@example.com"
                          required
                          :disabled="isSubmitting"
                        />
                      </div>

                      <div class="form-group">
                        <button
                          type="submit"
                          class="btn btn-primary btn-lg btn-block"
                          :disabled="!parentEmail || isSubmitting"
                          :class="{
                            'btn-disabled': !parentEmail || isSubmitting,
                          }"
                        >
                          <span v-if="isSubmitting">
                            <i class="fa fa-spinner fa-spin"></i> Sending...
                          </span>
                          <span v-else>Submit</span>
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>

              <!-- Step 2: Email Sent Confirmation -->
              <div v-else class="email-sent-confirmation text-center">
                <div class="success-icon mb-4">
                  <i
                    class="fa fa-check-circle text-success"
                    style="font-size: 64px"
                  ></i>
                </div>

                <h3>Email sent</h3>
                <p class="lead">
                  We've sent an email to your parent at
                  <strong class="text-primary">{{ parentEmail }}</strong>
                </p>
                <p>
                  Once they click the link and verify your account, you can
                  close this window and go to the
                  <router-link :to="{ name: 'login' }" class="text-primary"
                    >login page</router-link
                  >
                  to start using MyWhoosh.
                </p>

                <div class="actions mt-4">
                  <button
                    class="btn btn-default btn-lg mr-3"
                    @click="editEmail"
                    :disabled="isSubmitting"
                  >
                    Edit email address
                  </button>

                  <button
                    class="btn btn-primary btn-lg"
                    @click="resendEmail"
                    :disabled="isSubmitting || resendDisabled"
                    :class="{ 'btn-disabled': isSubmitting || resendDisabled }"
                  >
                    <span v-if="isSubmitting">
                      <i class="fa fa-spinner fa-spin"></i> Sending...
                    </span>
                    <span v-else-if="resendDisabled">
                      Resend in {{ formatTime(resendCountdown) }}
                    </span>
                    <span v-else>Resend Link</span>
                  </button>
                </div>
              </div>

              <!-- Error Display -->
              <div v-if="errorMessage" class="alert alert-danger mt-3">
                {{ errorMessage }}
              </div>
            </div>
          </section>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ParentalConsentPage",

  data() {
    return {
      parentEmail: "",
      emailSent: false,
      isSubmitting: false,
      errorMessage: "",
      childUserId: null,
      resendTimer: null,
      resendDisabled: false,
      resendCountdown: 0,
      redirectTimer: null,
      userAge: null,
    };
  },

  computed: {
    ...mapGetters("auth", ["user"]),
  },

  mounted() {
    document.body.classList.add("contact");

    // Get user data from route params or store
    if (this.$route.params.childUserId) {
      this.childUserId = this.$route.params.childUserId;
    }
    if (this.$route.params.userAge) {
      this.userAge = this.$route.params.userAge;
    }

    // If no child user ID, redirect to register
    if (!this.childUserId) {
      this.$router.push({ name: "register" });
      return;
    }
  },

  destroyed() {
    document.body.classList.remove("contact");
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
    if (this.redirectTimer) {
      clearTimeout(this.redirectTimer);
    }
  },

  methods: {
    startResendTimer() {
      this.resendDisabled = true;
      this.resendCountdown = 300; // 5 minutes in seconds

      this.resendTimer = setInterval(() => {
        this.resendCountdown--;
        if (this.resendCountdown <= 0) {
          this.resendDisabled = false;
          clearInterval(this.resendTimer);
        }
      }, 1000);
    },

    startRedirectTimer() {
      // Redirect to login after 12 minutes (720000ms)
      this.redirectTimer = setTimeout(() => {
        this.$router.push({ name: "login" });
      }, 720000); // 12 minutes
    },

    async checkInitialApprovalStatus() {
      try {
        const response = await this.$store.dispatch(
          "auth/checkParentalApproval",
          {
            childUserId: this.childUserId,
          }
        );

        if (response && response.data && response.data.approved) {
          // Parent has already approved, redirect immediately
          this.$router.push({ name: "login" });
        }
      } catch (error) {
        console.error("Error checking initial approval status:", error);
      }
    },

    async submitParentalConsent() {
      this.isSubmitting = true;
      this.errorMessage = "";

      try {
        const payload = {
          parentEmail: this.parentEmail,
          childUserId: this.childUserId,
          userAge: this.userAge,
        };

        const response = await this.$store.dispatch(
          "auth/submitParentalConsent",
          payload
        );

        if (response && response.status) {
          this.emailSent = true;
          this.startResendTimer(); // Start 5-minute timer for resend
          this.startRedirectTimer(); // Start 2-minute redirect timer
        } else {
          this.errorMessage =
            "There was an error sending the email. Please try again.";
        }
      } catch (error) {
        console.error("Error submitting parental consent:", error);
        this.errorMessage =
          "There was an error sending the email. Please try again.";
      } finally {
        this.isSubmitting = false;
      }
    },

    async resendEmail() {
      this.isSubmitting = true;
      this.errorMessage = "";

      try {
        const response = await this.$store.dispatch(
          "auth/resendParentalConsent",
          {
            parentEmail: this.parentEmail,
            childUserId: this.childUserId,
          }
        );

        if (response && response.status) {
          // Show success message temporarily
          const originalText = "Resend Link";
          this.$el.querySelector(".btn-primary").innerHTML =
            '<i class="fa fa-check"></i> Email Sent!';

          setTimeout(() => {
            if (this.$el.querySelector(".btn-primary")) {
              this.$el.querySelector(".btn-primary").innerHTML = originalText;
            }
          }, 3000);

          // Restart the resend timer
          this.startResendTimer();
        } else {
          this.errorMessage =
            "There was an error resending the email. Please try again.";
        }
      } catch (error) {
        console.error("Error resending email:", error);
        this.errorMessage =
          "There was an error resending the email. Please try again.";
      } finally {
        this.isSubmitting = false;
      }
    },

    editEmail() {
      this.emailSent = false;
      this.errorMessage = "";
      // Clear all timers
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
        this.resendTimer = null;
      }
      if (this.redirectTimer) {
        clearTimeout(this.redirectTimer);
        this.redirectTimer = null;
      }
      this.resendDisabled = false;
      this.resendCountdown = 0;
    },

    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    },
  },
};
</script>

<style scoped>
.parental-consent-form {
  padding: 40px 20px;
  max-width: 600px;
  margin: 0 auto;
}

.consent-title {
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 30px;
  color: #333;
  letter-spacing: 1px;
}

.consent-warning {
  border-left: 4px solid #f0ad4e;
  margin: 30px auto;
  max-width: 500px;
  padding: 20px;
  text-align: left;
}

.consent-instruction {
  font-size: 18px;
  font-weight: 300;
  line-height: 1.4;
  margin: 30px 0;
  color: #555;
}

.consent-form {
  max-width: 400px;
  margin: 0 auto;
}

.consent-input {
  height: 50px;
  font-size: 16px;
  padding: 12px 15px;
  border-radius: 5px;
  border: 2px solid #ddd;
}

.consent-input:focus {
  border-color: #ffd100;
  box-shadow: 0 0 0 0.2rem rgba(255, 209, 0, 0.25);
}

.consent-submit-btn {
  width: 100%;
  height: 50px;
  font-size: 18px;
  font-weight: bold;
  background-color: #ffd100;
  border-color: #ffd100;
  color: #333;
  border-radius: 5px;
}

.consent-submit-btn:hover:not(:disabled) {
  background-color: #ffcc00;
  border-color: #ffcc00;
  color: #333;
}

.email-sent-confirmation {
  padding: 40px 20px;
  max-width: 600px;
  margin: 0 auto;
}

.success-icon {
  margin-bottom: 20px;
}

.text-success {
  color: #5cb85c !important;
}

.text-primary {
  color: #337ab7 !important;
}

.btn-disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #cccccc !important;
  color: #666666 !important;
}

.actions {
  margin-top: 30px;
}

.mr-3 {
  margin-right: 15px;
}

.lead {
  font-size: 18px;
  font-weight: 300;
  line-height: 1.4;
}
</style>
